/**
 * TelehealthProductTile — standalone product tile for telehealth (GLP-1)
 * products. Cloned from `LmnProductTile` (clone-over-flags, per the PDP-variant
 * convention) rather than branched inline, so the telehealth treatment is a
 * self-contained unit: Rx badge, image, rating, name, Builder-authored price, a
 * "Get Started" CTA into intake, and a "Learn More" link to the PDP. NO
 * add-to-cart, NO quantity selector, NO swatches, NO quick view, NO favorites
 * heart (ET-225..232, ET-297).
 *
 * Dispatched by `HecProductTileCms` when `isTelehealthProduct(product)` is true,
 * so it applies across every tile surface (PLP, search, carousels, recs,
 * favorites, hybrid PDP).
 *
 * Everything shown here comes from SFCC except the price, which is authored in
 * Builder — see `telehealth-pricing.ts` for why that one field is the exception.
 * The Rx and secondary badges are ordinary `c_badging` / `c_secondBadging`
 * values rendered by the shared `ProductBadges`, so they need no code here.
 *
 * Layout note: the tile design (XD-224) is not finalized — element ORDER and
 * spacing below mirror `LmnProductTile` so the card sits correctly in the grid
 * alongside standard tiles. Revisit against XD-224 when it lands.
 */
import { useMemo } from "react";
import { Link, type LinkProps } from "react-router";

import { ProductImage } from "~/components/product-list/product-image";
import { useSite, useURL } from "~/contexts";
import { emitProductClick } from "~/hec/analytics/event-layer/gtm-product-click-events";
import { ProductTilePrice } from "~/hec/components/product-tile/product-tile-price";
import { isTreatmentAvailable } from "~/hec/telehealth/treatments";
import { useStartIntake } from "~/hec/telehealth/use-start-intake";
import type { HecProduct } from "~/hec/types/product";
import { getProductBadges } from "~/hec/types/product";
import { buildProductUrl } from "~/hec/utils/product-slug";
import { cn } from "~/lib/utils";
import { useLayoutData } from "~/routes/layout";
import { useFormatPrice } from "~/shop/utils/price";

import { ProductBadges } from "./product-badges";
import { Ratings } from "./ratings";
import { resolveTelehealthPriceDisplay } from "./telehealth-pricing";

/**
 * Every launch telehealth product is weight-loss (the only entry in the
 * treatment registry). The card is a marketing surface into the generic intake:
 * THS publishes exactly one active `WEIGHT_LOSS_INTAKE` questionnaire, at
 * variantType `COMBINED`, so there is no branded/compounded routing for the
 * tile to carry — verified against the dev THS API, where `BRANDED` and
 * `COMPOUNDED` both 400 for this condition.
 */
const TELEHEALTH_TREATMENT_SLUG = "weight-loss";

/** Non-PHI analytics context for the intake entry (`src` / `cta` params). */
const INTAKE_SOURCE = "product-tile";
const INTAKE_CTA_LOCATION = "tile-get-started";

interface TelehealthProductTileProps {
  product: HecProduct;
  /** Show ratings (default true; set false for basic/compact view). */
  enableRatings?: boolean;
  /** Star-color variant — "recs" on recommendation carousels, else "default". */
  ratingsVariant?: "default" | "recs";
  /** Site ID override — defaults to SiteContext site.id. */
  siteId?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Route prefetch strategy for the product link. */
  prefetch?: LinkProps["prefetch"];
  /** COM-158: above-the-fold tiles (first PLP row) render their image eager +
   *  fetchPriority="high" so they become LCP candidates. Default lazy. */
  imagePriority?: boolean;
  /** GTM product-click list context (SFCC-11026) — see {@link handleProductClick}. */
  gtmListId?: string;
  gtmListName?: string;
  gtmIndex?: number;
}

export function TelehealthProductTile({
  product,
  enableRatings = true,
  ratingsVariant = "default",
  siteId: siteIdProp,
  className,
  style,
  prefetch = "intent",
  imagePriority = false,
  gtmListId,
  gtmListName,
  gtmIndex,
}: TelehealthProductTileProps) {
  const url = useURL();
  const { site } = useSite();
  const { telehealthPricing } = useLayoutData();
  const formatPrice = useFormatPrice();
  const startIntake = useStartIntake();
  const siteId = siteIdProp ?? site.id;

  const badges = getProductBadges(product);
  const productName = product.name || product.productName || "";
  const productUrl = product.url || buildProductUrl(product.id, productName);
  const imageUrl = product.image_url || product.image?.link;
  const tileImage = product.image
    ? { link: product.image.link, alt: product.image.alt ?? productName }
    : imageUrl
      ? { link: imageUrl, alt: productName }
      : undefined;

  // Null when the SKU has no authored row — the tile then omits its price line
  // entirely and keeps the CTA. Deliberately NOT falling back to the SFCC
  // catalog price: that is the undiscounted number, and a wrong price on a
  // medication is worse than no price.
  //
  // Memoized for the same reason `HecProductTileCms` memoizes its own resolver:
  // the resolver returns a fresh object each call, so an inline call would defeat
  // the `memo` on <ProductTilePrice> on every render. `formatPrice` is
  // `useCallback`-stable and the layout data is per-navigation stable, so this
  // holds across a grid re-render.
  const priceDisplay = useMemo(
    () =>
      resolveTelehealthPriceDisplay(telehealthPricing[product.id], formatPrice),
    [telehealthPricing, product.id, formatPrice],
  );

  // ET-12's availability gate, applied to the RENDER as well as the click.
  // `startIntake` already returns false for an unavailable or unknown
  // treatment, but the return was discarded — so a `available: false` flip in
  // `treatments.ts` (the registry's own Coming Soon mechanism) would leave
  // every telehealth card with a primary CTA that silently does nothing and no
  // fallback affordance. Inert today, since weight-loss is available; this is
  // the fail-closed guard for the day it isn't. Learn More still renders, so
  // the PDP stays reachable.
  const canStartIntake = isTreatmentAvailable(TELEHEALTH_TREATMENT_SLUG);

  // GTM product-tile click (SFCC-11026) — same treatment as a standard PLP
  // tile, per Mike's call that telehealth PLPs report like any other PLP.
  // `emitProductClick` no-ops unless the surface supplied `gtmListId`, so
  // carousels/favorites stay silent exactly as they do for standard tiles.
  //
  // The price sent is the BUILDER price — the number the shopper actually saw —
  // not `resolvePriceFromHecProduct`'s SFCC catalog price, which a standard
  // tile would use. Those two genuinely differ for telehealth, and reporting a
  // price nobody was shown would be worse than the inconsistency.
  //
  // Which is why `custom` mode must report NO price rather than its resolved
  // `price: 0` (see `resolveTelehealthPriceDisplay`): `custom` renders verbatim
  // copy, so there was no number on screen, and a $0 product click would fan
  // out to GA4, UA and Segment as a real zero. `LmnProductTile` omits `price`
  // the same way for a product with no price concept.
  //
  // Discriminate on the resolved VALUE, not on `setPriceText` — `startsAt` sets
  // `setPriceText` too, and its figure WAS on screen, so it must keep reporting
  // that number.
  const handleProductClick = () => {
    const p = product as {
      master?: { masterId?: string };
      c_parentMasterProductID?: string;
    };
    emitProductClick(
      {
        id: p.master?.masterId ?? p.c_parentMasterProductID ?? product.id,
        name: productName,
        brand: product.brand,
        variant: null,
        price: priceDisplay?.price || undefined,
        category: "",
      },
      {
        listId: gtmListId ?? "",
        listName: gtmListName ?? gtmListId ?? "",
        index: gtmIndex ?? 1,
        siteId,
      },
    );
  };

  return (
    <div
      className={cn("overflow-hidden rounded-md bg-white", className)}
      style={style}
      data-testid={`product-tile-root-${product.id}`}
    >
      {/* Image container with badges. No favorites heart and no quick view for
          MVP; no OOS overlay by design — a telehealth product is not purchasable
          from the tile (intake gates the purchase), so it has no stock concept
          here. */}
      <div className="aspect-square relative w-full">
        {badges.length > 0 && (
          <ProductBadges badges={badges} productId={product.id} />
        )}

        <Link
          to={url(productUrl)}
          prefetch={prefetch}
          state={{ tileProduct: product }}
          className="block"
          data-testid={`product-tile-image-${product.id}`}
          onClick={handleProductClick}
        >
          {tileImage ? (
            <ProductImage
              image={tileImage}
              type="plp"
              priority={imagePriority}
            />
          ) : (
            <div className="aspect-square flex items-center justify-center rounded text-sm text-gray-400">
              No image
            </div>
          )}
        </Link>
      </div>

      {/* Tile body */}
      <div className="px-2 pb-2 pt-2 text-left">
        {enableRatings && (
          <div className="mt-2 flex">
            <Ratings
              rating={product.rating ?? 0}
              reviewCount={product.reviewCount}
              siteId={siteId}
              size="small"
              variant={ratingsVariant}
            />
          </div>
        )}

        <div className="mt-2 min-h-[3rem]">
          <Link
            to={url(productUrl)}
            prefetch={prefetch}
            state={{ tileProduct: product }}
            className="mb-[.5rem] line-clamp-2 font-grotesque font-medium leading-6 text-blue-04"
            data-testid={`product-tile-name-${product.id}`}
            onClick={handleProductClick}
          >
            {productName}
          </Link>
        </div>

        {/* `mb-[.5rem]` matches the standard tile, which puts the gap below the
            price rather than above whatever follows it. */}
        {priceDisplay && (
          <ProductTilePrice
            display={priceDisplay}
            className="mb-[.5rem]"
            data-testid={`product-tile-price-${product.id}`}
          />
        )}

        {/* Primary CTA. Routes through the shared ET-12 contract so the
            availability gate, the engagement-stall suppression and the src/cta
            params behave identically to every other intake entry point.

            `aria-label` names the product: a PLP renders eight of these in a
            grid, and "Get Started" alone gives a screen-reader user no way to
            tell them apart. */}
        {canStartIntake && (
          <button
            type="button"
            onClick={() =>
              startIntake({
                treatment: TELEHEALTH_TREATMENT_SLUG,
                source: INTAKE_SOURCE,
                ctaLocation: INTAKE_CTA_LOCATION,
              })
            }
            className="block w-full rounded bg-primary px-4 py-3 text-center font-medium text-primary-foreground"
            aria-label={`Get Started with ${productName}`}
            data-testid={`telehealth-tile-get-started-${product.id}`}
          >
            Get Started
          </button>
        )}

        {/* Secondary link. No "Learn More" exists on any tile today, so the
            treatment is borrowed verbatim from the only on-tile secondary link
            there is — `WhatsLmnButton`'s `text-sm text-accent underline` —
            rather than inventing one. Alignment/placement is an XD-224 question.

            Emits the product click like the image and name links do: same
            destination, and CIO already counts it either way —
            `PlpProductTile`'s `browse_result_click` beacon fires on any
            capture-phase click through an `a[href*=".html"]`, which this is. A
            silent GTM leg here would make CIO and GA4 click counts for
            telehealth tiles diverge with no visible cause. */}
        <Link
          to={url(productUrl)}
          prefetch={prefetch}
          state={{ tileProduct: product }}
          className="mt-2 inline-block text-sm text-accent underline"
          data-testid={`telehealth-tile-learn-more-${product.id}`}
          onClick={handleProductClick}
        >
          Learn More
        </Link>
      </div>
    </div>
  );
}
